import { h } from 'preact'
import { useState } from 'preact/hooks'
import classNames from 'classnames'
import { i18n, initI18n } from 'i18n/i18n'

import { ChosenItemTransfer } from 'models/exchange-transfer-calculate'
import { ExchangeDirection, SpinnerError } from 'models/calculator'

import {
  DEFAULT_EXCHANGE_DATA,
  DEFAULT_TRANSFER_TO_COUNTRY,
  REGISTRY_URL,
  DEFAULT_DEBOUNCE_TIME,
  DEFAULT_CHOSEN_ITEM_TRANSFER,
} from './config'

import { createRegistryUrl } from 'utils/urls'

import { ErrorSpinner } from 'common-modules/feedback/error-spinner'
import { CalculatorCtaButton } from 'common-modules/calculator-cta-button'
import { CalculatorCountrySelect } from 'common-modules/selects/calculator-country-select'
import { CalculatorTransferTypeSelect } from 'common-modules/selects/calculator-transfer-type-select'

import { useForeignSettings } from './hooks/useForeignSettings'
import { AmountName, useTransferCalculation } from './hooks/useTransferCalculation'

import { getCountryErrorMessage } from './utils/form-errors'

import CalculatorSummary from './components/calculator-summary'
import CalculatorInput from './components/calculator-input'

initI18n()

const TransferCalculator = () => {
  const [baseAmount, setBaseAmount] = useState(DEFAULT_EXCHANGE_DATA.baseAmount)
  const [counterAmount, setCounterAmount] = useState(DEFAULT_EXCHANGE_DATA.counterAmount)
  const [baseCurrency, setBaseCurrency] = useState(DEFAULT_EXCHANGE_DATA.baseCurrency)
  const [counterCurrency, setCounterCurrency] = useState(DEFAULT_EXCHANGE_DATA.counterCurrency)
  const [direction, setDirection] = useState<ExchangeDirection>(DEFAULT_EXCHANGE_DATA.direction)
  const [chosenCountryCode, setChosenCountryCode] = useState(DEFAULT_TRANSFER_TO_COUNTRY)
  const [chosenItemTransfer, setChosenItemTransfer] = useState<ChosenItemTransfer>(DEFAULT_CHOSEN_ITEM_TRANSFER)
  const [spinnerError, setSpinnerError] = useState<SpinnerError>({ isErrorVisible: false })

  const { domains, currencies, payoutOwner } = useForeignSettings({ setSpinnerError })
  const { exchangeCalculation, transferFees, apiErrorMessage, countdown, isCalculationLoading } =
    useTransferCalculation({
      baseAmount,
      counterAmount,
      baseCurrency,
      counterCurrency,
      direction,
      chosenCountryCode,
      chosenItemTransfer,
      domains,
      setBaseAmount,
      setCounterAmount,
      setChosenItemTransfer,
      setSpinnerError,
    })

  const handleAmountChange = (value: string, amountName: AmountName): void => {
    setDirection(amountName === 'baseAmount' ? 'forward' : 'back')
    if (amountName === 'baseAmount') {
      setBaseAmount(value)
    } else {
      setCounterAmount(value)
    }
  }

  const handleSetTransferType = (transferType: string, transferFee: string): void => {
    setChosenItemTransfer({ chosenItemTransferType: transferType, chosenItemTransferFee: transferFee })
  }

  const getEquivalentTransferFee = (): string | undefined =>
    transferFees.find((transferFee) => transferFee.transfer_type === chosenItemTransfer.chosenItemTransferType)
      ?.fee_original

  const getRegistryUrl = (): string => {
    const calculatorData = {
      soldCurrency: exchangeCalculation.sold_currency,
      soldAmount: exchangeCalculation.sold_amount,
      boughtCurrency: exchangeCalculation.bought_currency,
      boughtAmount: exchangeCalculation.bought_amount,
      returnTransferType: exchangeCalculation.transfer_type,
      calculateDirection: direction,
    }

    return createRegistryUrl(domains.registry || REGISTRY_URL, { calculatorData: JSON.stringify(calculatorData) })
  }

  const countryErrorMessage = getCountryErrorMessage(apiErrorMessage)

  return (
    <div className="transfer-calculator">
      <CalculatorInput
        id="transfer-calculator-base-amount"
        label={i18n('transferCalculator.amountYouSend')}
        isBaseCurrencyComponent={true}
        debounceTime={DEFAULT_DEBOUNCE_TIME}
        amount={baseAmount}
        baseCurrency={baseCurrency}
        counterCurrency={counterCurrency}
        apiErrorMessage={apiErrorMessage}
        currencies={currencies}
        receivedAmount={exchangeCalculation.sold_amount}
        isCalculationLoading={isCalculationLoading}
        handleAmountChange={handleAmountChange}
        handleSetBaseCurrency={setBaseCurrency}
        handleSetCounterCurrency={setCounterCurrency}
      />

      <div
        className={classNames('transfer-calculator__field', {
          'transfer-calculator__field--error': !!countryErrorMessage,
        })}
      >
        <p className="transfer-calculator__label">
          <b>{i18n('transferCalculator.countryYouSendMoney')}</b>
        </p>
        <CalculatorCountrySelect
          countries={payoutOwner}
          selectedCountryCode={chosenCountryCode}
          handleSetCountry={setChosenCountryCode}
        />
        {countryErrorMessage && <p className="transfer-calculator__error-text">{countryErrorMessage}</p>}
      </div>

      <div className="transfer-calculator__field">
        <p className="transfer-calculator__label">
          <b>{i18n('transferCalculator.foreignTransferType')}</b>
        </p>
        <CalculatorTransferTypeSelect
          transferFees={transferFees}
          chosenItemTransfer={chosenItemTransfer}
          handleSetTransferType={handleSetTransferType}
        />
      </div>

      <CalculatorInput
        id="transfer-calculator-counter-amount"
        label={i18n('transferCalculator.amountRecipientReceive')}
        isBaseCurrencyComponent={false}
        debounceTime={DEFAULT_DEBOUNCE_TIME}
        amount={counterAmount}
        baseCurrency={baseCurrency}
        counterCurrency={counterCurrency}
        apiErrorMessage={apiErrorMessage}
        currencies={currencies}
        receivedAmount={exchangeCalculation.sold_amount}
        isCalculationLoading={isCalculationLoading}
        handleAmountChange={handleAmountChange}
        handleSetBaseCurrency={setBaseCurrency}
        handleSetCounterCurrency={setCounterCurrency}
      />

      <CalculatorSummary
        exchangeCalculation={exchangeCalculation}
        equivalentFee={getEquivalentTransferFee()}
        countdown={countdown}
      />

      <CalculatorCtaButton
        href={getRegistryUrl()}
        label={i18n('transferCalculator.ctaButton')}
      />

      {spinnerError.isErrorVisible && <ErrorSpinner errorText={spinnerError.message} />}
    </div>
  )
}

export default TransferCalculator
